import { apiRequest, apiRequestPage } from './client';

/**
 * The Support module's client.
 *
 * Shapes mirror `ticketSummarySchema` / `ticketDetailSchema` in the API. They
 * are hand-declared here rather than pulled from the generated OpenAPI types
 * because the generated shapes are deeply nested and awkward to name at call
 * sites — but `npm run contracts:check` regenerates and diffs the schema in CI,
 * so a drift between these and the server fails the build.
 */

export type TicketStatus = 'new' | 'assigned' | 'in_progress' | 'resolved' | 'rejected';
export type TicketType =
  'complaint' | 'suggestion' | 'question' | 'technical' | 'purchase' | 'other';
export type TicketPriority = 'low' | 'med' | 'high';

export interface TicketSummary {
  id: string;
  subject: string;
  type: TicketType;
  priority: TicketPriority;
  status: TicketStatus;
  requester_id: string;
  requester_name: string;
  owner_company_id: string;
  owner_company_name: string;
  assignee_id: string | null;
  assignee_name: string | null;
  reopens: number;
  created_at: string;
  updated_at: string;
}

export interface TicketDetail extends TicketSummary {
  description: string;
  attachments: {
    id: string;
    name: string;
    mime_type: string;
    size_bytes: number;
    scan_status: 'pending' | 'clean' | 'infected' | 'failed';
  }[];
  notes: { id: string; author_name: string; text: string; created_at: string }[];
  history: {
    id: string;
    author_name: string;
    from_status: TicketStatus | null;
    to_status: TicketStatus;
    text: string | null;
    occurred_at: string;
  }[];
}

export interface TicketPage {
  items: TicketSummary[];
  total: number;
}

export async function listTickets(params: {
  page: number;
  pageSize: number;
  scope: 'all' | 'mine';
  status?: TicketStatus;
}): Promise<TicketPage> {
  const { items, meta } = await apiRequestPage<TicketSummary>('/tickets', {
    query: {
      page: params.page,
      page_size: params.pageSize,
      scope: params.scope,
      status: params.status,
    },
  });

  return { items, total: meta.total_items };
}

export function getTicket(id: string): Promise<TicketDetail> {
  return apiRequest<TicketDetail>(`/tickets/${id}`);
}

export function createTicket(body: {
  subject: string;
  description?: string;
  type: TicketType;
  priority: TicketPriority;
}): Promise<TicketDetail> {
  return apiRequest<TicketDetail>('/tickets', { method: 'POST', body });
}

export function transitionTicket(
  id: string,
  body: { to: Exclude<TicketStatus, 'new'>; note?: string },
): Promise<TicketDetail> {
  return apiRequest<TicketDetail>(`/tickets/${id}/transition`, { method: 'POST', body });
}

export function reopenTicket(id: string): Promise<TicketDetail> {
  return apiRequest<TicketDetail>(`/tickets/${id}/reopen`, { method: 'POST' });
}

export function addTicketNote(id: string, text: string): Promise<TicketDetail> {
  return apiRequest<TicketDetail>(`/tickets/${id}/notes`, { method: 'POST', body: { text } });
}

export function deleteTicket(id: string): Promise<void> {
  return apiRequest<void>(`/tickets/${id}`, { method: 'DELETE' });
}
